/** Admin → Sync tab API. Admin-gated on the backend. */
import axios from 'axios';
import type { HeadersGetter } from './customers';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

export interface SyncTargetStatus {
  target: string;
  source: string;            // 'bc' | 'shopify' | 'klaviyo'
  label: string;
  endpoint: string | null;
  record_count: number | null;
  last_synced_at: string | null;
  last_status: string | null;   // 'ok' | 'error'
  last_error: string | null;
}

export interface SyncConfig {
  cronExpression: string;
  enabled: boolean;
  updatedAt: string;
}

export async function getSyncStatus(getHeaders: HeadersGetter): Promise<SyncTargetStatus[]> {
  const headers = await getHeaders();
  const res = await axios.get<{ targets: SyncTargetStatus[] }>(`${API_BASE}/guest-admin/sync-status`, { headers });
  return res.data.targets;
}

/** Run a target; returns the refreshed status list. */
export async function runSync(getHeaders: HeadersGetter, target: string): Promise<SyncTargetStatus[]> {
  const headers = await getHeaders();
  const res = await axios.post<{ targets: SyncTargetStatus[] }>(`${API_BASE}/guest-admin/sync/${target}`, {}, { headers });
  return res.data.targets;
}

export async function getSyncConfig(getHeaders: HeadersGetter): Promise<SyncConfig> {
  const headers = await getHeaders();
  const res = await axios.get<SyncConfig>(`${API_BASE}/guest-admin/sync-config`, { headers });
  return res.data;
}

export async function setSyncConfig(getHeaders: HeadersGetter, cronExpression: string, enabled: boolean): Promise<SyncConfig> {
  const headers = await getHeaders();
  const res = await axios.put<SyncConfig>(`${API_BASE}/guest-admin/sync-config`, { cronExpression, enabled }, { headers });
  return res.data;
}

// ── Users & roles ────────────────────────────────────────────────────────────

export type AdminRole = 'admin' | 'office' | 'store';

export interface AdminUser {
  app_user_id: string;
  email: string | null;
  display_name: string | null;
  role: AdminRole;
  last_login: string | null;
  /** True = pre-provisioned, hasn't signed in yet. */
  pending: boolean;
  /** Set = removed (sign-in refused); row kept so authorship survives. */
  deactivated_at: string | null;
  /** May send outreach email as themselves. */
  can_email: boolean;
  store_codes: string[];
}

export async function listUsers(getHeaders: HeadersGetter): Promise<AdminUser[]> {
  const headers = await getHeaders();
  const res = await axios.get<{ users: AdminUser[] }>(`${API_BASE}/guest-admin/users`, { headers });
  return res.data.users;
}

export async function createUser(
  getHeaders: HeadersGetter,
  input: { email: string; displayName?: string; role: AdminRole; storeCodes: string[] }
): Promise<AdminUser[]> {
  const headers = await getHeaders();
  const res = await axios.post<{ users: AdminUser[] }>(`${API_BASE}/guest-admin/users`, input, { headers });
  return res.data.users;
}

export async function updateUser(
  getHeaders: HeadersGetter,
  id: string,
  patch: { role?: AdminRole; storeCodes?: string[]; canEmail?: boolean }
): Promise<AdminUser[]> {
  const headers = await getHeaders();
  const res = await axios.put<{ users: AdminUser[] }>(`${API_BASE}/guest-admin/users/${id}`, patch, { headers });
  return res.data.users;
}

/** Remove a user: invites are deleted outright; signed-in users are deactivated
 *  (sign-in refused, access stripped) with their name kept on all history. */
export async function deleteUser(getHeaders: HeadersGetter, id: string): Promise<AdminUser[]> {
  const headers = await getHeaders();
  const res = await axios.delete<{ users: AdminUser[] }>(`${API_BASE}/guest-admin/users/${id}`, { headers });
  return res.data.users;
}

/** Restore a removed user (stores must be re-assigned afterwards). */
export async function reactivateUser(getHeaders: HeadersGetter, id: string): Promise<AdminUser[]> {
  const headers = await getHeaders();
  const res = await axios.put<{ users: AdminUser[] }>(`${API_BASE}/guest-admin/users/${id}`, { reactivate: true }, { headers });
  return res.data.users;
}
